import type { DisplayMessage } from '../../types/agent';

/** Words common enough to tell French from English in a short request, rare enough not to appear in the other
 *  (the same lists as sculpt-api `sculpt/agent/tools/ask.py`, plus a few audit words). */
const MARKERS: Record<'fr' | 'en', Set<string>> = {
  fr: new Set(['le', 'la', 'les', 'des', 'du', 'une', 'un', 'est', 'sont', 'quel', 'quelle', 'quels', 'quelles', 'dans',
    'pour', 'avec', 'sur', 'ce', 'cette', 'que', 'qui', 'vous', 'aux', 'au', 'par', 'plus', 'ajoute', 'ajouter',
    'peux', 'peut', 'combien', 'prix', 'nouvelle', 'nouveau', 'fichier', 'modèle', 'classeur', 'corrige', 'vérifie']),
  en: new Set(['the', 'is', 'are', 'a', 'an', 'of', 'and', 'to', 'you', 'your', 'what', 'which', 'this', 'that', 'with',
    'for', 'from', 'should', 'would', 'can', 'please', 'add', 'new', 'how', 'does', 'do', 'it', 'price', 'file',
    'model', 'workbook', 'check']),
};

/** The pane's own messages (the audit card's Fix button), which say nothing about the user's language. */
const PANE_MESSAGE = /^(Fix|Corrige) F\d+ \(/;

/** 'fr', 'en', or null when the text has no marker word or as many of each. */
export function guessLanguage(text: string): 'fr' | 'en' | null {
  const words = text.toLowerCase().match(/[a-zà-ÿœ']+/g) ?? [];
  const fr = words.filter((w) => MARKERS.fr.has(w)).length;
  const en = words.filter((w) => MARKERS.en.has(w)).length;
  if (fr === en) return null;
  return fr > en ? 'fr' : 'en';
}

/** The language the user writes this conversation in: that of their latest message that tells, skipping the
 *  pane's own messages. A message the pane sends on the user's behalf goes out in it, so the agent does not
 *  switch to the UI's language (English by default) mid-conversation. */
export function conversationLanguage(messages: readonly DisplayMessage[]): 'fr' | 'en' | null {
  for (let i = messages.length - 1; i >= 0; i--) {
    const m = messages[i];
    if (m.role !== 'user' || PANE_MESSAGE.test(m.content)) continue;
    const lang = guessLanguage(m.content);
    if (lang) return lang;
  }
  return null;
}
